import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
    Users,
    Search,
    Trash2,
    Edit,
    Loader2,
    AlertTriangle,
    UserPlus,
    LogIn
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function UserManager() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
    const [editModal, setEditModal] = useState({ open: false, user: null });
    const [createModal, setCreateModal] = useState(false);
    const [loginAsModal, setLoginAsModal] = useState({ open: false, user: null });
    const [loginAsLoading, setLoginAsLoading] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', email: '' });
    const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const getToken = () => localStorage.getItem('superadmin_token');

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/superadmin/users`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Gagal memuat data user');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.user) return;
        
        try {
            await axios.delete(`${API_BASE}/api/superadmin/users/${deleteModal.user.id}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('User berhasil dihapus');
            setDeleteModal({ open: false, user: null });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal menghapus user');
        }
    };

    const handleEdit = async () => {
        if (!editModal.user) return;
        
        try {
            await axios.put(`${API_BASE}/api/superadmin/users/${editModal.user.id}`, editForm, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('User berhasil diupdate');
            setEditModal({ open: false, user: null });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal mengupdate user');
        }
    };

    const handleCreate = async () => {
        if (!createForm.name || !createForm.email || !createForm.password) {
            toast.error('Semua field harus diisi');
            return;
        }

        setCreating(true);
        try {
            await axios.post(`${API_BASE}/api/superadmin/users`, createForm, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('User berhasil dibuat');
            setCreateModal(false);
            setCreateForm({ name: '', email: '', password: '' });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal membuat user');
        } finally {
            setCreating(false);
        }
    };

    const openEditModal = (user) => {
        setEditForm({ name: user.name, email: user.email });
        setEditModal({ open: true, user });
    };

    const handleLoginAs = async () => {
        if (!loginAsModal.user) return;
        setLoginAsLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE}/api/superadmin/users/${loginAsModal.user.id}/login-as`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            const { token } = response.data;
            localStorage.setItem('login_as_token', token);
            localStorage.setItem('login_as_user', JSON.stringify({ name: loginAsModal.user.name, email: loginAsModal.user.email }));
            setLoginAsModal({ open: false, user: null });
            toast.success(`Membuka aplikasi sebagai ${loginAsModal.user.name}...`);
            window.open('/', '_blank');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal membuat sesi login');
        } finally {
            setLoginAsLoading(false);
        }
    };

    const filteredUsers = users.filter(user => 
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white">User Manager</h1>
                    <p className="text-slate-400 mt-1">Kelola semua user di sistem</p>
                </div>
                <Button
                    onClick={() => setCreateModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Tambah User
                </Button>
            </div>

            {/* Search & Stats */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Cari user..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                    />
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>{filteredUsers.length} users</span>
                </div>
            </div>

            {/* Users Table */}
            <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-700 hover:bg-slate-700/50">
                                <TableHead className="text-slate-400">Nama</TableHead>
                                <TableHead className="text-slate-400">Email</TableHead>
                                <TableHead className="text-slate-400">Dibuat</TableHead>
                                <TableHead className="text-slate-400 text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsers.map((user) => (
                                <TableRow key={user.id} className="border-slate-700 hover:bg-slate-700/50">
                                    <TableCell className="text-white font-medium">{user.name}</TableCell>
                                    <TableCell className="text-slate-300">{user.email}</TableCell>
                                    <TableCell className="text-slate-400">
                                        {new Date(user.created_at).toLocaleDateString('id-ID')}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setLoginAsModal({ open: true, user })}
                                                className="text-slate-400 hover:text-green-500 hover:bg-green-500/10"
                                                title="Login sebagai user ini"
                                            >
                                                <LogIn className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => openEditModal(user)}
                                                className="text-slate-400 hover:text-amber-500 hover:bg-amber-500/10"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setDeleteModal({ open: true, user })}
                                                className="text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                                        Tidak ada user ditemukan
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Create User Modal */}
            <Dialog open={createModal} onOpenChange={setCreateModal}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-amber-500" />
                            Tambah User Baru
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Buat akun user baru untuk mengakses sistem
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300">Nama</Label>
                            <Input
                                value={createForm.name}
                                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                placeholder="Masukkan nama lengkap"
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Email</Label>
                            <Input
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                placeholder="email@example.com"
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Password</Label>
                            <Input
                                type="password"
                                value={createForm.password}
                                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                placeholder="Minimal 6 karakter"
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setCreateModal(false); setCreateForm({ name: '', email: '', password: '' }); }}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={creating}
                            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                        >
                            {creating ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Membuat...
                                </>
                            ) : (
                                'Buat User'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, user: null })}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Konfirmasi Hapus
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Apakah Anda yakin ingin menghapus user <strong className="text-white">{deleteModal.user?.name}</strong>? 
                            Tindakan ini tidak dapat dibatalkan.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModal({ open: false, user: null })}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600 text-white"
                        >
                            Hapus
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Login As Modal */}
            <Dialog open={loginAsModal.open} onOpenChange={(open) => setLoginAsModal({ open, user: null })}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <LogIn className="w-5 h-5 text-green-500" />
                            Login sebagai User
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Anda akan membuka aplikasi sebagai <strong className="text-white">{loginAsModal.user?.name}</strong> ({loginAsModal.user?.email}) di tab baru. Sesi superadmin di tab ini tidak terpengaruh.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setLoginAsModal({ open: false, user: null })}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleLoginAs}
                            disabled={loginAsLoading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {loginAsLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Memproses...
                                </>
                            ) : (
                                <>
                                    <LogIn className="w-4 h-4 mr-2" />
                                    Buka sebagai User Ini
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, user: null })}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white">Edit User</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300">Nama</Label>
                            <Input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Email</Label>
                            <Input
                                value={editForm.email}
                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditModal({ open: false, user: null })}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleEdit}
                            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                        >
                            Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
